/**
 * Markdown ↔ Lumen block model (P-520, P-524, P-528). Pure code over the editor's block JSON
 * (`{ id, type, props, content, children }`), so import and export run anywhere (app, server, tests).
 *
 * Notion-style conventions: children are indented four spaces under their parent, toggles are
 * `<details>`, callouts `<aside>`, block equations `$$`, to-dos `- [ ]`, tables GFM pipes.
 */

export interface MdStyles {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  code?: boolean;
}

export type MdInline =
  | { type: 'text'; text: string; styles: MdStyles }
  | { type: 'link'; href: string; content: Array<{ type: 'text'; text: string; styles: MdStyles }> }
  | { type: 'inlineEquation'; props: { latex: string } }
  | { type: 'mention'; props: Record<string, string> };

export interface MdTableContent {
  type: 'tableContent';
  columnWidths?: Array<number | null>;
  headerRows?: number;
  rows: Array<{ cells: Array<MdInline[] | { type: 'tableCell'; content: MdInline[]; props?: Record<string, unknown> }> }>;
}

export interface MdBlock {
  id?: string;
  type: string;
  props?: Record<string, unknown>;
  content?: MdInline[] | MdTableContent | undefined;
  children?: MdBlock[];
}

export interface MarkdownExportOptions {
  /** Link target and title for page blocks, links to pages and page mentions. */
  pageLink?: (pageId: string) => { title: string; href: string } | null;
  /** Link target and title for inline databases. */
  databaseLink?: (dbId: string) => { title: string; href: string } | null;
}

// ───────────── export ─────────────

const ESCAPE = /[\\`*_[\]~$<]/g;

function escapeText(t: string): string {
  return t.replace(ESCAPE, (c) => `\\${c}`);
}

function styled(text: string, s: MdStyles): string {
  if (!text) return '';
  if (s.code) return `\`${text.replace(/`/g, 'ˋ')}\``;
  // Keep surrounding spaces outside the markers so the result stays valid Markdown.
  const m = /^(\s*)(.*?)(\s*)$/s.exec(text)!;
  let core = escapeText(m[2]!);
  if (!core) return text;
  if (s.underline) core = `<u>${core}</u>`;
  if (s.strike) core = `~~${core}~~`;
  if (s.italic) core = `_${core}_`;
  if (s.bold) core = `**${core}**`;
  return `${m[1]}${core}${m[3]}`;
}

export function inlineToMarkdown(content: MdInline[] | undefined, opts: MarkdownExportOptions = {}): string {
  if (!content) return '';
  return content
    .map((c) => {
      if (c.type === 'text') return styled(c.text, c.styles ?? {});
      if (c.type === 'link') return `[${c.content.map((t) => styled(t.text, t.styles ?? {})).join('')}](${c.href})`;
      if (c.type === 'inlineEquation') return `$${c.props.latex}$`;
      if (c.type === 'mention') {
        const p = c.props;
        if (p.kind === 'date') return `@${p.date}${p.time ? ` ${p.time}` : ''}`;
        if (p.kind === 'page') {
          const l = opts.pageLink?.(p.id ?? '');
          return l ? `[${escapeText(l.title)}](${l.href})` : '';
        }
        return `@${p.name ?? p.id ?? ''}`;
      }
      return '';
    })
    .join('');
}

function prefixLines(text: string, first: string, rest: string): string {
  return text
    .split('\n')
    .map((l, i) => (i === 0 ? first : rest) + l)
    .join('\n');
}

function startEscape(line: string): string {
  return /^(#{1,6} |[-+*] |\d+[.)] |> |---$|\$\$|```|<details>|<aside>|\|)/.test(line) ? `\\${line}` : line;
}

function cellInline(cell: MdTableContent['rows'][number]['cells'][number]): MdInline[] {
  return Array.isArray(cell) ? cell : cell.content;
}

function blockToMarkdown(b: MdBlock, opts: MarkdownExportOptions, index: number): string {
  const p = b.props ?? {};
  const text = Array.isArray(b.content) ? inlineToMarkdown(b.content, opts) : '';
  switch (b.type) {
    case 'heading': {
      const level = Math.min(3, Number(p.level ?? 1));
      return `${'#'.repeat(level)} ${text}`;
    }
    case 'bulletListItem':
      return prefixLines(text, '- ', '  ');
    case 'numberedListItem':
      return prefixLines(text, `${index}. `, '   ');
    case 'checkListItem':
      return prefixLines(text, p.checked ? '- [x] ' : '- [ ] ', '  ');
    case 'toggleListItem':
      return `<details>\n<summary>${text}</summary>`;
    case 'quote':
      return prefixLines(text, '> ', '> ');
    case 'codeBlock': {
      const raw = Array.isArray(b.content) ? b.content.map((c) => (c.type === 'text' ? c.text : '')).join('') : '';
      const fence = raw.includes('```') ? '````' : '```';
      return `${fence}${p.language && p.language !== 'text' ? String(p.language) : ''}\n${raw}\n${fence}`;
    }
    case 'divider':
      return '---';
    case 'callout':
      return `<aside>\n${p.icon ? `${String(p.icon)} ` : ''}${text}`;
    case 'equation':
      return `$$\n${String(p.latex ?? '')}\n$$`;
    case 'image':
      return `![${escapeText(String(p.caption || p.name || ''))}](${String(p.url ?? '')})`;
    case 'video':
    case 'audio':
    case 'file':
    case 'pdf':
      return p.url ? `[${escapeText(String(p.name || p.url))}](${String(p.url)})` : '';
    case 'bookmark':
    case 'embed':
      return p.url ? `[${escapeText(String(p.title || p.url))}](${String(p.url)})` : '';
    case 'page':
    case 'linkToPage': {
      const l = opts.pageLink?.(String(p.pageId ?? ''));
      return l ? `[${escapeText(l.title)}](${l.href})` : '';
    }
    case 'database': {
      const l = opts.databaseLink?.(String(p.dbId ?? ''));
      return l ? `[${escapeText(l.title)}](${l.href})` : '';
    }
    case 'table': {
      const t = b.content as MdTableContent | undefined;
      if (!t?.rows.length) return '';
      const cells = t.rows.map((r) => r.cells.map((c) => inlineToMarkdown(cellInline(c), opts).replace(/\|/g, '\\|').replace(/\n/g, '<br>')));
      const width = Math.max(...cells.map((r) => r.length));
      const row = (r: string[]) => `| ${Array.from({ length: width }, (_, i) => r[i] ?? '').join(' | ')} |`;
      return [row(cells[0]!), `| ${Array.from({ length: width }, () => '---').join(' | ')} |`, ...cells.slice(1).map(row)].join('\n');
    }
    case 'toc':
    case 'breadcrumb':
    case 'button':
    case 'syncedBlock':
    case 'columnList':
    case 'column':
      return '';
    default:
      return text
        .split('\n')
        .map((l) => startEscape(l))
        .join('  \n');
  }
}

/** Block model → Markdown. */
export function blocksToMarkdown(blocks: MdBlock[], opts: MarkdownExportOptions = {}): string {
  const out: string[] = [];
  let number = 0;
  for (const b of blocks) {
    // Column layouts and synced blocks have no Markdown form: their content is written in place.
    if (b.type === 'columnList' || b.type === 'column' || b.type === 'syncedBlock') {
      const inner = blocksToMarkdown(b.children ?? [], opts);
      if (inner.trim()) out.push(inner.trimEnd());
      number = 0;
      continue;
    }
    number = b.type === 'numberedListItem' ? number + 1 : 0;
    const own = blockToMarkdown(b, opts, b.type === 'numberedListItem' && b.props?.start ? Number(b.props.start) : number);
    const kids = b.children?.length ? blocksToMarkdown(b.children, opts).trimEnd() : '';
    if (b.type === 'toggleListItem') {
      out.push(`${own}\n\n${kids ? `${indent(kids)}\n\n` : ''}</details>`);
    } else if (b.type === 'callout') {
      out.push(`${own}${kids ? `\n\n${indent(kids)}` : ''}\n</aside>`);
    } else {
      if (!own && !kids && b.type !== 'paragraph') continue;
      out.push(kids ? `${own}\n\n${indent(kids)}` : own);
    }
  }
  return out.join('\n\n') + '\n';
}

function indent(s: string): string {
  return s
    .split('\n')
    .map((l) => (l ? `    ${l}` : l))
    .join('\n');
}

// ───────────── import ─────────────

function text(t: string, styles: MdStyles = {}): MdInline {
  return { type: 'text', text: t, styles: { ...styles } };
}

function pushText(out: MdInline[], t: string, styles: MdStyles) {
  if (!t) return;
  const last = out[out.length - 1];
  if (last?.type === 'text' && JSON.stringify(last.styles) === JSON.stringify(styles)) last.text += t;
  else out.push(text(t, styles));
}

/** Parses inline Markdown (emphasis, code, strike, underline, links, inline equations, escapes). */
export function parseInline(src: string, styles: MdStyles = {}): MdInline[] {
  const out: MdInline[] = [];
  let i = 0;
  let buf = '';
  const flush = () => {
    pushText(out, buf, styles);
    buf = '';
  };
  const findClose = (marker: string, from: number) => {
    for (let j = from; j <= src.length - marker.length; j++) {
      if (src[j] === '\\') {
        j++;
        continue;
      }
      if (src.startsWith(marker, j)) return j;
    }
    return -1;
  };
  while (i < src.length) {
    const c = src[i]!;
    if (c === '\\' && i + 1 < src.length && /[!-/:-@[-`{-~]/.test(src[i + 1]!)) {
      buf += src[i + 1];
      i += 2;
      continue;
    }
    if (src.startsWith('<br>', i)) {
      buf += '\n';
      i += 4;
      continue;
    }
    if (c === '`') {
      const end = src.indexOf('`', i + 1);
      if (end > i) {
        flush();
        pushText(out, src.slice(i + 1, end), { ...styles, code: true });
        i = end + 1;
        continue;
      }
    }
    const pairs: Array<[string, keyof MdStyles]> = [
      ['**', 'bold'],
      ['__', 'bold'],
      ['~~', 'strike'],
      ['*', 'italic'],
      ['_', 'italic'],
    ];
    let matched = false;
    for (const [m, style] of pairs) {
      if (!src.startsWith(m, i)) continue;
      // `_` inside words (snake_case) is not emphasis.
      if (m === '_' && i > 0 && /\w/.test(src[i - 1]!)) continue;
      const end = findClose(m, i + m.length);
      if (end > i + m.length - 1 && end !== i + m.length) {
        flush();
        for (const x of parseInline(src.slice(i + m.length, end), { ...styles, [style]: true })) mergeInto(out, x);
        i = end + m.length;
        matched = true;
        break;
      }
    }
    if (matched) continue;
    if (src.startsWith('<u>', i)) {
      const end = src.indexOf('</u>', i + 3);
      if (end > 0) {
        flush();
        for (const x of parseInline(src.slice(i + 3, end), { ...styles, underline: true })) mergeInto(out, x);
        i = end + 4;
        continue;
      }
    }
    if (c === '$' && src[i + 1] !== '$') {
      const end = findClose('$', i + 1);
      if (end > i + 1) {
        flush();
        out.push({ type: 'inlineEquation', props: { latex: src.slice(i + 1, end) } });
        i = end + 1;
        continue;
      }
    }
    if (c === '[') {
      const close = findClose(']', i + 1);
      if (close > 0 && src[close + 1] === '(') {
        const end = src.indexOf(')', close + 2);
        if (end > 0) {
          flush();
          const label = parseInline(src.slice(i + 1, close), styles).filter((x): x is Extract<MdInline, { type: 'text' }> => x.type === 'text');
          out.push({ type: 'link', href: src.slice(close + 2, end), content: label.length ? label : [text(src.slice(close + 2, end), styles) as Extract<MdInline, { type: 'text' }>] });
          i = end + 1;
          continue;
        }
      }
    }
    if (c === '<') {
      const auto = /^<(https?:\/\/[^>\s]+)>/.exec(src.slice(i));
      if (auto) {
        flush();
        out.push({ type: 'link', href: auto[1]!, content: [text(auto[1]!, styles) as Extract<MdInline, { type: 'text' }>] });
        i += auto[0].length;
        continue;
      }
    }
    buf += c;
    i++;
  }
  flush();
  return out;
}

function mergeInto(out: MdInline[], x: MdInline) {
  if (x.type === 'text') pushText(out, x.text, x.styles);
  else out.push(x);
}

interface Line {
  indent: number;
  text: string;
}

function toLines(md: string): Line[] {
  return md
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((raw) => {
      const expanded = raw.replace(/\t/g, '    ');
      const indent = expanded.length - expanded.trimStart().length;
      return { indent, text: expanded.trimStart() };
    });
}

function dedent(lines: Line[], by: number): Line[] {
  return lines.map((l) => (l.text ? { indent: Math.max(0, l.indent - by), text: l.text } : l));
}

/** Lines that belong to the block starting at `start` (more indented, or blank between them). */
function childLines(lines: Line[], start: number, base: number): { child: Line[]; next: number } {
  const child: Line[] = [];
  let i = start;
  while (i < lines.length) {
    const l = lines[i]!;
    if (!l.text) {
      // A blank line ends the children unless more indented lines follow.
      let j = i + 1;
      while (j < lines.length && !lines[j]!.text) j++;
      if (j < lines.length && lines[j]!.indent > base) {
        for (; i < j; i++) child.push(lines[i]!);
        continue;
      }
      break;
    }
    if (l.indent <= base) break;
    child.push(l);
    i++;
  }
  const min = Math.min(...child.filter((l) => l.text).map((l) => l.indent));
  return { child: Number.isFinite(min) ? dedent(child, min) : [], next: i };
}

const TABLE_SEP = /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;

function splitRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  const cells: string[] = [];
  let cur = '';
  for (let i = 0; i < trimmed.length; i++) {
    if (trimmed[i] === '\\' && trimmed[i + 1] === '|') {
      cur += '|';
      i++;
    } else if (trimmed[i] === '|') {
      cells.push(cur.trim());
      cur = '';
    } else cur += trimmed[i];
  }
  cells.push(cur.trim());
  return cells;
}

function parseLines(lines: Line[]): MdBlock[] {
  const blocks: MdBlock[] = [];
  let i = 0;
  const withChildren = (b: MdBlock, base: number) => {
    const { child, next } = childLines(lines, i, base);
    if (child.some((l) => l.text)) b.children = parseLines(child);
    i = next;
    blocks.push(b);
  };
  while (i < lines.length) {
    const l = lines[i]!;
    const t = l.text;
    if (!t) {
      i++;
      continue;
    }
    // Fenced code.
    const fence = /^(`{3,}|~{3,})\s*([\w+#.-]*)/.exec(t);
    if (fence) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !lines[i]!.text.startsWith(fence[1]!)) {
        const raw = lines[i]!;
        body.push(raw.text ? ' '.repeat(Math.max(0, raw.indent - l.indent)) + raw.text : '');
        i++;
      }
      i++;
      blocks.push({ type: 'codeBlock', props: { language: fence[2] || 'text' }, content: body.length ? [text(body.join('\n'))] : [] });
      continue;
    }
    if (t === '$$' || (t.startsWith('$$') && t.endsWith('$$') && t.length > 4)) {
      if (t !== '$$') {
        blocks.push({ type: 'equation', props: { latex: t.slice(2, -2).trim() } });
        i++;
        continue;
      }
      const body: string[] = [];
      i++;
      while (i < lines.length && lines[i]!.text !== '$$') body.push(lines[i++]!.text);
      i++;
      blocks.push({ type: 'equation', props: { latex: body.join('\n') } });
      continue;
    }
    if (t.startsWith('<details')) {
      // <details><summary>Title</summary> … </details>
      let summary = '';
      const inner: Line[] = [];
      const sameLine = /<summary>(.*?)<\/summary>/.exec(t);
      if (sameLine) summary = sameLine[1]!;
      i++;
      let depth = 1;
      while (i < lines.length) {
        const x = lines[i]!.text;
        if (x.startsWith('<details')) depth++;
        if (x.startsWith('</details>') && --depth === 0) break;
        const s = /^<summary>(.*?)<\/summary>$/.exec(x);
        if (s && !summary && depth === 1) summary = s[1]!;
        else inner.push(lines[i]!);
        i++;
      }
      i++;
      const min = Math.min(...inner.filter((x) => x.text).map((x) => x.indent));
      const block: MdBlock = { type: 'toggleListItem', content: parseInline(summary) };
      if (inner.some((x) => x.text)) block.children = parseLines(dedent(inner, Number.isFinite(min) ? min : 0));
      blocks.push(block);
      continue;
    }
    if (t.startsWith('<aside>')) {
      const inner: Line[] = [];
      const rest = t.slice(7).replace(/<\/aside>$/, '');
      if (rest.trim()) inner.push({ indent: 0, text: rest.trim() });
      const closedOnSameLine = t.endsWith('</aside>');
      i++;
      while (!closedOnSameLine && i < lines.length && !lines[i]!.text.startsWith('</aside>')) inner.push(lines[i++]!);
      if (!closedOnSameLine) i++;
      const first = inner.findIndex((x) => x.text);
      let icon = '';
      let body = first >= 0 ? inner[first]!.text : '';
      const emoji = /^(\p{Extended_Pictographic}️?)\s*/u.exec(body);
      if (emoji) {
        icon = emoji[1]!;
        body = body.slice(emoji[0].length);
      }
      const block: MdBlock = { type: 'callout', props: { icon: icon || '💡' }, content: parseInline(body) };
      const kids = inner.slice(first + 1);
      const min = Math.min(...kids.filter((x) => x.text).map((x) => x.indent));
      if (kids.some((x) => x.text)) block.children = parseLines(dedent(kids, Number.isFinite(min) ? min : 0));
      blocks.push(block);
      continue;
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(t);
    if (heading) {
      i++;
      withChildren({ type: 'heading', props: { level: Math.min(3, heading[1]!.length) }, content: parseInline(heading[2]!.replace(/\s+#+\s*$/, '')) }, l.indent);
      continue;
    }
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(t)) {
      blocks.push({ type: 'divider' });
      i++;
      continue;
    }
    // Tables.
    if (t.startsWith('|') && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1]!.text)) {
      const rows: string[][] = [splitRow(t)];
      i += 2;
      while (i < lines.length && lines[i]!.text.startsWith('|')) rows.push(splitRow(lines[i++]!.text));
      blocks.push({ type: 'table', content: { type: 'tableContent', headerRows: 1, rows: rows.map((r) => ({ cells: r.map((c) => ({ type: 'tableCell' as const, content: parseInline(c), props: {} })) })) } });
      continue;
    }
    const todo = /^[-*+]\s+\[([ xX])\]\s?(.*)$/.exec(t);
    if (todo) {
      const { textLines, next } = continuation(lines, i, l.indent, todo[2]!);
      i = next;
      withChildren({ type: 'checkListItem', props: { checked: todo[1] !== ' ' }, content: parseInline(textLines) }, l.indent);
      continue;
    }
    const bullet = /^[-*+]\s+(.*)$/.exec(t);
    if (bullet) {
      const { textLines, next } = continuation(lines, i, l.indent, bullet[1]!);
      i = next;
      withChildren({ type: 'bulletListItem', content: parseInline(textLines) }, l.indent);
      continue;
    }
    const numbered = /^(\d+)[.)]\s+(.*)$/.exec(t);
    if (numbered) {
      const { textLines, next } = continuation(lines, i, l.indent, numbered[2]!);
      i = next;
      withChildren({ type: 'numberedListItem', content: parseInline(textLines) }, l.indent);
      continue;
    }
    if (t.startsWith('>')) {
      const body: string[] = [];
      while (i < lines.length && lines[i]!.text.startsWith('>') && lines[i]!.indent === l.indent) body.push(lines[i++]!.text.replace(/^>\s?/, ''));
      withChildren({ type: 'quote', content: parseInline(body.join('\n')) }, l.indent);
      continue;
    }
    const image = /^!\[(.*?)\]\((\S+?)(?:\s+"[^"]*")?\)$/.exec(t);
    if (image) {
      blocks.push({ type: 'image', props: { url: image[2]!, caption: image[1]!.replace(/\\(.)/g, '$1') } });
      i++;
      continue;
    }
    // Paragraph: consecutive lines at the same indentation.
    const body: string[] = [];
    while (i < lines.length && lines[i]!.text && lines[i]!.indent === l.indent && !isBlockStart(lines[i]!.text, lines[i + 1]?.text)) {
      body.push(lines[i]!.text.replace(/ {2}$/, ''));
      i++;
    }
    if (!body.length) {
      body.push(t);
      i++;
    }
    withChildren({ type: 'paragraph', content: parseInline(body.join('\n')) }, l.indent);
  }
  return blocks;
}

/** List item text continues on following lines indented under the marker (no blank line). */
function continuation(lines: Line[], start: number, base: number, first: string): { textLines: string; next: number } {
  const parts = [first];
  let i = start + 1;
  while (i < lines.length && lines[i]!.text && lines[i]!.indent > base && lines[i]!.indent < base + 4 && !isBlockStart(lines[i]!.text, lines[i + 1]?.text)) {
    parts.push(lines[i]!.text);
    i++;
  }
  return { textLines: parts.join('\n'), next: i };
}

function isBlockStart(t: string, next?: string): boolean {
  return (
    /^(#{1,6}\s|[-*+]\s|\d+[.)]\s|>|```|~~~|\$\$|<details|<aside|<\/details|<\/aside)/.test(t) ||
    /^(-{3,}|\*{3,}|_{3,})$/.test(t) ||
    /^!\[.*\]\(.*\)$/.test(t) ||
    (t.startsWith('|') && !!next && TABLE_SEP.test(next))
  );
}

/** Markdown → block model. */
export function markdownToBlocks(md: string): MdBlock[] {
  return parseLines(toLines(md));
}

/** Title from a Markdown document: its first `# ` heading, if it starts with one. */
export function markdownTitle(md: string): { title: string | null; body: string } {
  const m = /^\s*#\s+(.+)\n?/.exec(md);
  return m ? { title: m[1]!.trim(), body: md.slice(m[0].length) } : { title: null, body: md };
}
